/**
 * 관리자: 이용권별 권한(platform_plan_permissions) 조회·저장.
 * 기본 기능(유료/무료 구분 기능) 권한은 extra.features 에 둔다(src/constants/planFeatures.ts).
 * 쓰기는 RLS(is_platform_admin) 로 관리자만 가능.
 */
import { supabase } from '../supabaseClient';
import type { AccountPassCategory } from '../utils/subscriptionPassStorage';
import {
  defaultPlanFeatures,
  parsePlanAiFeatures,
  parsePlanFeatures,
  PLAN_AI_FEATURE_KEYS,
  PLAN_FEATURE_KEYS,
  type PlanAiFeatureMap,
  type PlanFeatureKey,
  type PlanFeatureMap,
} from '../constants/planFeatures';
import { PLAN_FEATURE_MIRRORS, PLAN_TABLE_ITEMS, planItemOn, type PlanAiColumn, type PlanTableItem } from '../constants/planTableItems';
import { FREE_IMAGE_STORAGE_LIMIT_BYTES, PASS_IMAGE_STORAGE_LIMIT_BYTES } from './slideImageStorageService';

/** 서버 check 제약과 같은 범위(1MB ~ 10GB) */
export const STORAGE_LIMIT_MIN_BYTES = 1048576;
export const STORAGE_LIMIT_MAX_BYTES = 10737418240;

export const PLAN_PERMISSION_KEYS: AccountPassCategory[] = ['free', 'beta', 'eventFree', 'eventPaid', 'individual', 'group'];

/**
 * PLUS 이용권 = free 가 아닌 모든 분류(베타 테스터 무료·이벤트 무료·이벤트 유료·개별 유료·그룹 유료).
 * 권한은 모두 같다: 관리자 화면의 「PLUS 이용권」 열을 저장하면 이 행들에 같은 값을 쓰고,
 * 읽을 때도(usePlanGateRows) 하나로 합쳐 쓴다. 서버(DB 함수)는 행별로 읽지만 행 값이 항상 같다
 * (관리자 저장 + 마이그레이션 20261027230000 의 통일·동기화 트리거).
 */
export const PLUS_PLAN_KEYS: AccountPassCategory[] = PLAN_PERMISSION_KEYS.filter((k) => k !== 'free');
/** 관리자 화면·비교표에서 「PLUS 이용권」 열을 대표하는 행 */
export const PLUS_CANONICAL_PLAN_KEY: AccountPassCategory = 'individual';
export const isPlusPlanKey = (k: AccountPassCategory): boolean => k !== 'free';

export type PlanPermissionRow = {
  plan_key: AccountPassCategory;
  ai_learning_plan: boolean;
  ai_worksheet: boolean;
  ai_slides: boolean;
  ai_other: boolean;
  ai_monthly_credits: number;
  max_classes: number;
  /** 이미지 저장 용량 한도(바이트). null = 열 없음(20261027190000 마이그레이션 전) */
  storage_limit_bytes: number | null;
  /** 기본 기능 권한(extra.features). 값이 없으면 기존 규칙(프리 = 잠금) */
  features: PlanFeatureMap;
  /** AI 세부 기능(extra.features.ai_*). 값이 없는 키(마이그레이션·저장 전)는 ai_other 를 따른다 */
  ai_features: PlanAiFeatureMap;
  /** extra 의 다른 키 보존용 */
  extra?: Record<string, unknown> | null;
  updated_at?: string | null;
};

/** 마이그레이션 시드와 같은 기본값(행이 없을 때 표시용) */
export function defaultPlanPermission(plan: AccountPassCategory): PlanPermissionRow {
  const free = plan === 'free';
  return {
    plan_key: plan,
    ai_learning_plan: false,
    ai_worksheet: false,
    ai_slides: false,
    ai_other: false,
    ai_monthly_credits: free ? 0 : 3000,
    max_classes: free ? 1 : 6,
    storage_limit_bytes: free ? FREE_IMAGE_STORAGE_LIMIT_BYTES : PASS_IMAGE_STORAGE_LIMIT_BYTES,
    features: defaultPlanFeatures(free),
    ai_features: {},
  };
}

function clampInt(v: unknown, min: number, max: number, fallback: number): number {
  const x = Math.round(Number(v));
  if (!Number.isFinite(x)) return fallback;
  return Math.min(max, Math.max(min, x));
}

export function normalizePlanPermission(raw: Partial<PlanPermissionRow> & { plan_key: AccountPassCategory }): PlanPermissionRow {
  const d = defaultPlanPermission(raw.plan_key);
  const extra = raw.extra && typeof raw.extra === 'object' && !Array.isArray(raw.extra) ? raw.extra : null;
  const stored = raw.features ?? parsePlanFeatures(extra);
  const features = { ...d.features };
  for (const k of PLAN_FEATURE_KEYS) {
    if (typeof stored[k] === 'boolean') features[k] = stored[k] as boolean;
  }
  const storedAi = raw.ai_features ?? parsePlanAiFeatures(extra);
  const ai_features: PlanAiFeatureMap = {};
  for (const k of PLAN_AI_FEATURE_KEYS) {
    if (typeof storedAi[k] === 'boolean') ai_features[k] = storedAi[k];
  }
  return {
    plan_key: raw.plan_key,
    ai_learning_plan: raw.ai_learning_plan === true,
    ai_worksheet: raw.ai_worksheet === true,
    ai_slides: raw.ai_slides === true,
    ai_other: raw.ai_other === true,
    ai_monthly_credits: clampInt(raw.ai_monthly_credits, 0, 10_000_000, d.ai_monthly_credits),
    max_classes: clampInt(raw.max_classes, 1, 100, d.max_classes),
    storage_limit_bytes:
      raw.storage_limit_bytes === null
        ? null
        : clampInt(raw.storage_limit_bytes, STORAGE_LIMIT_MIN_BYTES, STORAGE_LIMIT_MAX_BYTES, d.storage_limit_bytes ?? PASS_IMAGE_STORAGE_LIMIT_BYTES),
    features,
    ai_features,
    extra,
    updated_at: raw.updated_at ?? null,
  };
}

export async function listPlanPermissions(): Promise<PlanPermissionRow[]> {
  const { data, error } = await supabase
    .from('platform_plan_permissions')
    .select('plan_key, ai_learning_plan, ai_worksheet, ai_slides, ai_other, ai_monthly_credits, max_classes, extra, updated_at');
  if (error) throw error;
  const byKey = new Map<string, PlanPermissionRow>();
  for (const row of (data ?? []) as PlanPermissionRow[]) {
    if (PLAN_PERMISSION_KEYS.includes(row.plan_key)) byKey.set(row.plan_key, normalizePlanPermission(row));
  }
  // 저장 용량 열은 따로 읽는다(마이그레이션 전이면 열이 없어 전체 조회가 실패하지 않도록).
  const storage = await fetchPlanStorageLimits();
  return PLAN_PERMISSION_KEYS.map((k) => {
    const row = byKey.get(k) ?? defaultPlanPermission(k);
    if (!storage) return { ...row, storage_limit_bytes: null };
    const v = storage[k];
    return typeof v === 'number' ? { ...row, storage_limit_bytes: v } : row;
  });
}

/** 이용권별 저장 용량 한도(바이트). 열이 없거나 읽기 실패면 null. */
export async function fetchPlanStorageLimits(): Promise<Partial<Record<AccountPassCategory, number>> | null> {
  try {
    const { data, error } = await supabase.from('platform_plan_permissions').select('plan_key, storage_limit_bytes');
    if (error || !Array.isArray(data)) return null;
    const out: Partial<Record<AccountPassCategory, number>> = {};
    for (const raw of data as { plan_key?: unknown; storage_limit_bytes?: unknown }[]) {
      if (typeof raw.plan_key !== 'string' || raw.storage_limit_bytes == null) continue;
      const n = Number(raw.storage_limit_bytes);
      if (Number.isFinite(n) && n > 0) out[raw.plan_key as AccountPassCategory] = Math.round(n);
    }
    return out;
  } catch {
    return null;
  }
}

function existingFeatures(extra: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const f = extra?.features;
  return f && typeof f === 'object' && !Array.isArray(f) ? (f as Record<string, unknown>) : {};
}

export async function savePlanPermissions(rows: PlanPermissionRow[]): Promise<void> {
  if (!rows.length) return;
  const payload = rows.map((r) => {
    const n = normalizePlanPermission(r);
    return {
      plan_key: n.plan_key,
      ai_learning_plan: n.ai_learning_plan,
      ai_worksheet: n.ai_worksheet,
      ai_slides: n.ai_slides,
      ai_other: n.ai_other,
      ai_monthly_credits: n.ai_monthly_credits,
      max_classes: n.max_classes,
      // 열이 있을 때만(null = 마이그레이션 전) 저장 용량을 보낸다
      ...(n.storage_limit_bytes === null ? {} : { storage_limit_bytes: n.storage_limit_bytes }),
      // extra 의 다른 키·features 의 모르는 키는 그대로 두고, 기본 기능 + (값이 정해진) AI 세부 기능만 갱신
      extra: { ...(n.extra ?? {}), features: { ...existingFeatures(n.extra), ...n.features, ...n.ai_features } },
    };
  });
  const { error } = await supabase.from('platform_plan_permissions').upsert(payload, { onConflict: 'plan_key' });
  if (error) throw error;
}

/** AI 열 ↔ 행 필드 */
export const AI_COLUMN_FIELD: Record<PlanAiColumn, 'ai_learning_plan' | 'ai_worksheet' | 'ai_slides' | 'ai_other'> = {
  learningPlan: 'ai_learning_plan',
  worksheet: 'ai_worksheet',
  slides: 'ai_slides',
  other: 'ai_other',
};

/** 비교표 항목 켜짐 여부(묶인 키가 모두 켜져 있을 때만 켜짐) */
export function planRowItemOn(item: PlanTableItem, r: PlanPermissionRow): boolean {
  return (
    planItemOn(
      item,
      {
        features: r.features,
        ai: { learningPlan: r.ai_learning_plan, worksheet: r.ai_worksheet, slides: r.ai_slides, other: r.ai_other },
        aiFeatures: r.ai_features,
      },
      r.plan_key === 'free'
    ) ?? false
  );
}

/** 항목을 켜고 끌 때 바꿀 필드(묶인 키를 한꺼번에) */
export function planItemPatch(item: PlanTableItem, r: PlanPermissionRow, on: boolean): Partial<PlanPermissionRow> {
  const b = item.binding;
  if (b.kind === 'features') {
    const features = { ...r.features };
    for (const k of b.keys) features[k] = on;
    return { features };
  }
  if (b.kind === 'aiColumns') {
    const next: Partial<PlanPermissionRow> = {};
    for (const c of b.columns) next[AI_COLUMN_FIELD[c]] = on;
    return next;
  }
  return { ai_features: { ...r.ai_features, [b.key]: on } };
}

/**
 * 묶인 키를 항목 값(모두 켜짐일 때만 켜짐)으로 맞추고, AI 세부 기능은 명시 값으로 채운다.
 * 표에 없는 예전 값도 항목에서 정한다(관리자 화면에 따로 보이지 않음):
 * - dashboard_presentation 등 PLAN_FEATURE_MIRRORS = 대응 항목 키와 같은 값
 * - ai_other 열 = AI 세부 기능(ai_curriculum_input·ai_evaluation·ai_record_feedback) 중 하나라도 켜짐.
 *   서버·화면 판정은 세부 키를 쓰고, ai_other 는 세부 키가 없던 예전 행의 대체값으로만 남는다.
 */
export function normalizePlanRowToTable(row: PlanPermissionRow): PlanPermissionRow {
  let r = normalizePlanPermission(row);
  for (const item of PLAN_TABLE_ITEMS) r = { ...r, ...planItemPatch(item, r, planRowItemOn(item, r)) };
  const features = { ...r.features };
  for (const [legacy, source] of Object.entries(PLAN_FEATURE_MIRRORS) as [PlanFeatureKey, PlanFeatureKey][]) {
    features[legacy] = features[source];
  }
  const ai_other = PLAN_AI_FEATURE_KEYS.some((k) => r.ai_features[k] === true);
  return { ...r, features, ai_other };
}

const PLUS_BOOL_FIELDS = ['ai_learning_plan', 'ai_worksheet', 'ai_slides', 'ai_other'] as const;

/**
 * PLUS 계열 행들을 하나로(가장 넓은 권한: 기능은 하나라도 켜져 있으면 켬, 숫자는 최댓값).
 * 저장 용량 열이 없으면(null) null 유지.
 */
export function mergePlusPermission(rows: PlanPermissionRow[]): PlanPermissionRow {
  const plus = rows.filter((r) => isPlusPlanKey(r.plan_key)).map(normalizePlanPermission);
  const base = plus.find((r) => r.plan_key === PLUS_CANONICAL_PLAN_KEY) ?? plus[0] ?? defaultPlanPermission(PLUS_CANONICAL_PLAN_KEY);
  if (!plus.length) return base;
  const out: PlanPermissionRow = { ...base, features: { ...base.features }, ai_features: { ...base.ai_features } };
  for (const f of PLUS_BOOL_FIELDS) out[f] = plus.some((r) => r[f]);
  out.ai_monthly_credits = Math.max(...plus.map((r) => r.ai_monthly_credits));
  out.max_classes = Math.max(...plus.map((r) => r.max_classes));
  out.storage_limit_bytes = plus.some((r) => r.storage_limit_bytes === null)
    ? null
    : Math.max(...plus.map((r) => r.storage_limit_bytes as number));
  for (const k of PLAN_FEATURE_KEYS) out.features[k] = plus.some((r) => r.features[k]);
  for (const k of PLAN_AI_FEATURE_KEYS) {
    if (plus.some((r) => typeof r.ai_features[k] === 'boolean')) {
      out.ai_features[k] = plus.some((r) => (typeof r.ai_features[k] === 'boolean' ? r.ai_features[k] : r.ai_other));
    }
  }
  return out;
}

/** 모든 PLUS 계열 행을 같은 값으로(각 행의 extra 다른 키·updated_at 은 유지) */
export function unifyPlusPermissions(rows: PlanPermissionRow[]): PlanPermissionRow[] {
  const merged = mergePlusPermission(rows);
  return rows.map((r) =>
    isPlusPlanKey(r.plan_key)
      ? {
          ...merged,
          features: { ...merged.features },
          ai_features: { ...merged.ai_features },
          plan_key: r.plan_key,
          extra: r.extra,
          updated_at: r.updated_at,
        }
      : r
  );
}

/** PLUS 계열 행끼리 다른 필드 이름(관리자 안내용) */
export function plusPermissionDifferences(rows: PlanPermissionRow[]): string[] {
  const plus = rows.filter((r) => isPlusPlanKey(r.plan_key)).map(normalizePlanPermission);
  if (plus.length < 2) return [];
  const diffs: string[] = [];
  const differs = (get: (r: PlanPermissionRow) => unknown) => new Set(plus.map((r) => JSON.stringify(get(r) ?? null))).size > 1;
  // ai_other·예전 키(PLAN_FEATURE_MIRRORS)는 저장 시 항목 값에서 다시 정해지므로 비교하지 않는다
  for (const f of ['ai_learning_plan', 'ai_worksheet', 'ai_slides', 'ai_monthly_credits', 'max_classes', 'storage_limit_bytes'] as const) {
    if (differs((r) => r[f])) diffs.push(f);
  }
  for (const k of PLAN_FEATURE_KEYS) if (!PLAN_FEATURE_MIRRORS[k] && differs((r) => r.features[k])) diffs.push(k);
  for (const k of PLAN_AI_FEATURE_KEYS) if (differs((r) => r.ai_features[k] ?? r.ai_other)) diffs.push(k);
  return diffs;
}
